import { SignUpFormField } from "../constants/signup-form-field.enum";
import { SignUpStep } from "../constants/signup-step.enum";
import { GymSubtype, GymType } from "../../../constants/gymSubtypes";
import { SignupFlowFormData } from "../../../hooks/useRevenueHero";
import Cookies from "js-cookie";
import { getGymTypeFromSubtype } from "../../../utils/gymTypes";

/**
 * If using a `Multiple checkboxes` field type in Hubspot, their API requires
 * selected options to be joined with a semicolon, no spaces needed
 */
export const formatHubspotMultiSelectOptions = (options: string[]): string => {
  return options ? options.join(";") : "";
};

export const formatUrl = (url: string | null): string | null => {
  if (!url) {
    return undefined;
  }

  const formattedUrl = url.trim();
  const hasProtocol = /^https?:\/\//i;

  if (!hasProtocol.test(formattedUrl)) {
    return `https://${formattedUrl}`;
  }
  return formattedUrl;
};

/**
 * Create an analytics payload for the current step
 * @param formData - The form data
 * @param currentStepName - The current step name
 * @returns The analytics payload
 */
export const createAnalyticsPayload = (
  context: any,
  currentStepName: string,
) => {
  const analyticsData = {
    step: currentStepName,
    value: context?.formData[currentStepName],
  };

  switch (currentStepName) {
    case SignUpStep.BusinessNeeds: {
      const val = context?.formData[SignUpFormField.BusinessNeeds];
      if (Array.isArray(val)) {
        analyticsData.value = val.join(", ");
      } else {
        analyticsData.value = val;
      }
      break;
    }
    case SignUpStep.Company: {
      const val = context?.formData[SignUpFormField.Company];
      if (val && typeof val === "object") {
        analyticsData.value = val?.label?.split(",")[0] || "";
      }
      break;
    }
    default: {
      analyticsData.value = context?.formData[currentStepName];
      break;
    }
  }
  return analyticsData;
};

export const getRevenueHeroFormData = (
  currentFormData: any,
): SignupFlowFormData => {
  const gymSubtype: GymSubtype = String(
    currentFormData[SignUpFormField.Vertical],
  ) as GymSubtype;
  const freeFormUserInput = {
    companyName: String(
      currentFormData[SignUpFormField.Company].label.split(",")[0],
    ).trim(),
    firstName: String(currentFormData[SignUpFormField.FirstName]).trim(),
    lastName: String(currentFormData[SignUpFormField.LastName]).trim(),
    email: String(currentFormData[SignUpFormField.Email]).trim(),
    phoneNumber: currentFormData[SignUpFormField.Phone],
    websiteUrl: formatUrl(currentFormData[SignUpFormField.Url]),
  };
  const predefinedValues = {
    country: currentFormData[SignUpFormField.Country],
    gymType: getGymTypeFromSubtype(gymSubtype) as GymType,
    gymSubtype: gymSubtype as GymSubtype,
    yearsOpen: currentFormData[SignUpFormField.YearsOpen],
    monthlyRevenue: currentFormData[SignUpFormField.Revenue],
    businessNeeds: formatHubspotMultiSelectOptions(
      currentFormData[SignUpFormField.BusinessNeeds],
    ),
    currentSoftware: currentFormData[SignUpFormField.CurrentSoftware] || "",
  };
  const utmParams = {
    utmSource: Cookies.get("utm_source") || "",
    utmMedium: Cookies.get("utm_medium") || "",
    utmCampaign: Cookies.get("utm_campaign") || "",
    utmContent: Cookies.get("utm_content") || "",
    utmTerm: Cookies.get("utm_term") || "",
    hubspotUtk: Cookies.get("hubspotutk") || "",
  };
  return { ...freeFormUserInput, ...predefinedValues, ...utmParams };
};
